import { useCallback } from 'react';

import { useOneClickAddToBag } from '../../../../hooks/useOneClickAddToBag';

import type { GetSharedLineItemProductForRestaurantQuery } from '../../GraphQL/ShareLineItemScreen.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const useShareLineItemQuickAddToBag = (params: Params) => {
  const {
    deliveryDetails,
    lineItemQuantity,
    productForRestaurant,
    navigateToLocationSearch,
    onAddProductToBagSuccess,
  } = params;

  // ─────────────────────────────────────────────────────────────────────

  const { addProductToBag, isAddingProductToBag } = useOneClickAddToBag({
    restaurantSlug: productForRestaurant?.restaurant.slug ?? '',
    deliveryDetails,
    onSuccess: onAddProductToBagSuccess,
  });

  // ─── Handlers ────────────────────────────────────────────────────────

  const quickAddToBag = useCallback(() => {
    if (!productForRestaurant) {
      navigateToLocationSearch();

      return;
    }

    void addProductToBag(productForRestaurant, lineItemQuantity);
  }, [addProductToBag, lineItemQuantity, navigateToLocationSearch, productForRestaurant]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    isAddingProductToBag,
    quickAddToBag,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  deliveryDetails?: DeliveryDetails;
  lineItemQuantity: number;
  navigateToLocationSearch: () => void;
  onAddProductToBagSuccess: () => void;
  productForRestaurant?: ProductForRestaurant;
};

type DeliveryDetails = {
  addressId: string;
  addressName: string | null;
  completeAddress: string;
  vendorRestaurantId: string;
  vendor: string;
  deliveryFee: number;
  deliveryEstimate?: string | null;
  tip: number;
};

type ProductForRestaurant = NonNullable<
  GetSharedLineItemProductForRestaurantQuery['productForRestaurant']
>;
